import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import OrderMobileCardShell from "../../Order/OrderMobileCardShell";
import FuturesAccountPanel from "../FuturesAccountPanel";
import {
    FUTURES_WALLET_HISTORY_DEFAULT_FILTERS,
    FUTURES_WALLET_TX_TYPE_OPTIONS,
    formatFuturesWalletTxAmount,
    formatFuturesWalletTxType,
    futuresWalletTxAmountClass,
} from "../futuresWalletHistoryHelpers";
import {
    decNum as futuresDecNum,
    findFuturesContractInPairs,
} from "../futuresIncrementHelpers";
import FuturesClosePositionModal from "../FuturesClosePositionModal";
import FuturesAdjustIsolatedMarginModal from "../FuturesAdjustIsolatedMarginModal";
import {
    bindPositionInitialMargin,
    bindPositionMarginRatioPct,
    formatMarginModeLabel,
    formatMarginRatioPct,
    isIsolatedMarginMode,
} from "../futuresSymbolMarginHelpers";

function FuturesDateInput({ value, onChange, placeholder = "From", className = "", ...props }) {
    return (
        <div className="agce-margin-date-input-wrap" data-placeholder={placeholder} data-empty={!value}>
            <input
                type="date"
                value={value}
                onChange={onChange}
                className={`agce-margin-filter-input ${className}`.trim()}
                {...props}
            />
        </div>
    );
}

/** Open Orders — horizontal chip row with left/right arrows on small screens (matches OrdersSubTabs pattern). */
function SpotOpenOrdersFilterRow({
    SPOT_OPEN_ORDER_KINDS,
    openOrderKindTab,
    setOpenOrderKindTab,
    orderType,
    setorderType,
}) {
    const scrollerRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = useMemo(
        () => () => {
            const el = scrollerRef.current;
            if (!el) return;
            const max = el.scrollWidth - el.clientWidth;
            const left = el.scrollLeft;
            setCanLeft(left > 2);
            setCanRight(max > 2 && left < max - 2);
        },
        []
    );

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        updateArrows();
        el.addEventListener("scroll", updateArrows, { passive: true });
        window.addEventListener("resize", updateArrows);
        const t = window.setTimeout(updateArrows, 150);
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
        if (ro) ro.observe(el);
        return () => {
            window.clearTimeout(t);
            el.removeEventListener("scroll", updateArrows);
            window.removeEventListener("resize", updateArrows);
            if (ro) ro.disconnect();
        };
    }, [updateArrows, SPOT_OPEN_ORDER_KINDS.length, openOrderKindTab, orderType]);

    const scrollByAmount = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const amount = Math.max(160, Math.floor(el.clientWidth * 0.65));
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    return (
        <div className="spot_orders_filter_bar">
            <div className="spot_orders_filter_scrollWrap">
                <button
                    type="button"
                    className={`spot_orders_chipsArrow spot_orders_chipsArrow--left${canLeft ? "" : " is-hidden"}`}
                    aria-label="Scroll filters left"
                    onClick={() => scrollByAmount(-1)}
                >
                    <i className="ri-arrow-left-s-line" aria-hidden="true" />
                </button>

                <div className="spot_orders_filter_chips spot_orders_filter_chips--scroll" ref={scrollerRef}>
                    {SPOT_OPEN_ORDER_KINDS.map((k) => (
                        <button
                            key={k.id}
                            type="button"
                            className={`spot_orders_chip ${openOrderKindTab === k.id ? "is-active" : ""}`}
                            onClick={() => setOpenOrderKindTab(k.id)}
                        >
                            {k.label}
                        </button>
                    ))}

                    <div className="spot_orders_filter_actions">
                        <CustomFilterDropdown
                            options={[
                                { value: "All", label: "All Sides" },
                                { value: "BUY", label: "Buy" },
                                { value: "SELL", label: "Sell" }
                            ]}
                            value={orderType}
                            onChange={(val) => setorderType(val)}
                            label="All Sides"
                        />
                        <button
                            type="button"
                            className="spot_orders_reset"
                            onClick={() => {
                                setOpenOrderKindTab("all");
                                setorderType("All");
                            }}
                        >
                            <i className="ri-refresh-line" aria-hidden="true" />
                            Reset
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    className={`spot_orders_chipsArrow spot_orders_chipsArrow--right${canRight ? "" : " is-hidden"}`}
                    aria-label="Scroll filters right"
                    onClick={() => scrollByAmount(1)}
                >
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}

/** Same filter shell as isolated margin — inline bar on desktop, bottom sheet on mobile. */
function FilterBarScrollable({ children }) {
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    return (
        <div className="agce-margin-filter-container">
            <div className="desktop_view2">
                <div className="d-flex align-items-center w-100 agce-margin-filter-bar">
                    {children}
                </div>
            </div>
            <div className="agce-margin-mobile-filter-wrap">
                <div className="agce-margin-mobile-filter-header" style={{ padding: "10px 16px 0px" }}>
                    <button type="button" className="agce-margin-filter-toggle-btn agce-futures-filter-toggle-btn" onClick={() => setIsPopupOpen(true)}>
                        <i className="ri-filter-3-line" /> Filter
                    </button>
                </div>
                {isPopupOpen ? (
                    <div
                        className="futures-margin-modal position-fixed w-100 h-100 top-0 start-0"
                        style={{ zIndex: 9999, background: "rgba(0,0,0,0.5)" }}
                        onClick={() => setIsPopupOpen(false)}
                    >
                        <div
                            className="cntnt bottom-margin-modal agce-margin-filter-popup position-absolute bottom-0 w-100"
                            style={{ background: "var(--bg-primary, #fff)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header border-0 pb-0 pt-3 px-4">
                                <h6>Filters</h6>
                                <button type="button" className="btn-close" onClick={() => setIsPopupOpen(false)} />
                            </div>
                            <div className="modal-body p-4 agce-margin-filter-popup-content">
                                {children}
                            </div>
                            <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex">
                                <button
                                    type="button"
                                    className="btn btn-primary w-100 agce-margin-filter-apply-btn"
                                    onClick={() => setIsPopupOpen(false)}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

const WALLET_HIST_PRESET_LABELS = ["1D", "7D", "30D", "90D"];

const walletHistToLocalIso = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function CustomFilterDropdown({ options, value, onChange, label }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value) || options[0];

    return (
        <div className="agce-custom-select-container" ref={containerRef}>
            <div 
                className="agce-custom-select-trigger" 
                onClick={() => setIsOpen(!isOpen)}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span>{selectedOption ? selectedOption.label : label}</span>
                <i className={`ri-arrow-down-s-line ${isOpen ? 'open' : ''}`}></i>
            </div>
            {isOpen && (
                <ul className="agce-custom-select-options" role="listbox">
                    {options.map((o) => (
                        <li 
                            key={o.value || "all"} 
                            className={`agce-custom-select-option ${o.value === value ? 'selected' : ''}`}
                            onClick={() => {
                                onChange(o.value);
                                setIsOpen(false);
                            }}
                            role="option"
                            aria-selected={o.value === value}
                        >
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function FuturesWalletHistoryFilterBar({ filters, setFilters, marginAsset, contractSymbol }) {
    const [preset, setPreset] = useState("");

    const assetOptions = useMemo(() => {
        const opts = [{ value: "", label: "All Assets" }];
        if (marginAsset) {
            opts.push({ value: marginAsset, label: marginAsset });
        }
        return opts;
    }, [marginAsset]);

    const symbolOptions = useMemo(() => {
        const opts = [{ value: "", label: "All Contracts" }];
        if (contractSymbol) {
            opts.push({ value: contractSymbol, label: contractSymbol });
        }
        return opts;
    }, [contractSymbol]);

    const patch = (key, value) => {
        if (key === "from" || key === "to") setPreset("");
        setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    };

    const applyPreset = (p) => {
        const now = new Date();
        const days = p === "1D" ? 1 : p === "7D" ? 7 : p === "30D" ? 30 : 90;
        const start = new Date(now);
        start.setDate(now.getDate() - days);
        setPreset(p);
        setFilters((prev) => ({
            ...prev,
            from: walletHistToLocalIso(start),
            to: walletHistToLocalIso(now),
            page: 1,
        }));
    };

    const reset = () => {
        setPreset("");
        setFilters({ ...FUTURES_WALLET_HISTORY_DEFAULT_FILTERS });
    };

    return (
        <FilterBarScrollable>
            <CustomFilterDropdown
                options={FUTURES_WALLET_TX_TYPE_OPTIONS}
                value={filters.type}
                onChange={(val) => patch("type", val)}
                label="Transaction type"
            />
            <CustomFilterDropdown
                options={assetOptions}
                value={filters.asset}
                onChange={(val) => patch("asset", val)}
                label="Asset"
            />
            <CustomFilterDropdown
                options={symbolOptions}
                value={filters.symbol}
                onChange={(val) => patch("symbol", val)}
                label="Contract"
            />
            <CustomFilterDropdown
                options={[
                    { value: "", label: "Time Range" },
                    { value: "1D", label: "1D" },
                    { value: "7D", label: "7D" },
                    { value: "30D", label: "30D" },
                    { value: "90D", label: "90D" }
                ]}
                value={preset}
                onChange={(val) => applyPreset(val)}
                label="Time Range"
            />
            <FuturesDateInput
                value={filters.from}
                placeholder="From"
                onChange={(e) => patch("from", e.target.value)}
                aria-label="From date"
            />
            <span className="agce-margin-filter-arrow">→</span>
            <FuturesDateInput
                value={filters.to}
                placeholder="To"
                onChange={(e) => patch("to", e.target.value)}
                aria-label="To date"
            />
            <button type="button" className="agce-margin-filter-reset" onClick={reset}>
                Reset <i className="ri-refresh-line" aria-hidden="true" />
            </button>
        </FilterBarScrollable>
    );
}

/** Primary tabs row (Open Orders / Order History / …) — arrows when list overflows on mobile */
function SpotOrdersPrimaryTabsScroll({ positionOrderTab, setPositionOrderTab, positionsCount = 0, openOrdersCount = 0 }) {
    const scrollerRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = useMemo(
        () => () => {
            const el = scrollerRef.current;
            if (!el) return;
            const max = el.scrollWidth - el.clientWidth;
            const left = el.scrollLeft;
            setCanLeft(left > 2);
            setCanRight(max > 2 && left < max - 2);
        },
        []
    );

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        updateArrows();
        el.addEventListener("scroll", updateArrows, { passive: true });
        window.addEventListener("resize", updateArrows);
        const t = window.setTimeout(updateArrows, 150);
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
        if (ro) ro.observe(el);
        return () => {
            window.clearTimeout(t);
            el.removeEventListener("scroll", updateArrows);
            window.removeEventListener("resize", updateArrows);
            if (ro) ro.disconnect();
        };
    }, [updateArrows, positionOrderTab, positionsCount, openOrdersCount]);

    const scrollByAmount = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const amount = Math.max(120, Math.floor(el.clientWidth * 0.6));
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    return (
        <div className="spot_orders_primary_tabs_scrollWrap">
            <button
                type="button"
                className={`spot_orders_chipsArrow spot_orders_chipsArrow--left${canLeft ? "" : " is-hidden"}`}
                aria-label="Scroll tabs left"
                onClick={() => scrollByAmount(-1)}
            >
                <i className="ri-arrow-left-s-line" aria-hidden="true" />
            </button>

            <ul className="position_list spot_orders_primary_tabs spot_orders_primary_tabs--scroller" ref={scrollerRef}>
                <li className={`nav-item ${positionOrderTab === "positions" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("positions")}>
                        {`Positions(${positionsCount})`}
                    </button>
                </li>
                <li className={`nav-item ${positionOrderTab === "positionHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("positionHistory")}>
                        Position History
                    </button>
                </li>
                <li className={`nav-item ${positionOrderTab === "openOrders" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("openOrders")}>
                        {`Open Orders(${openOrdersCount})`}
                    </button>
                </li>
                <li className={`nav-item ${positionOrderTab === "orderHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("orderHistory")}>
                        Order History
                    </button>
                </li>
                <li className={`nav-item ${positionOrderTab === "tradeHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("tradeHistory")}>
                        Trade History
                    </button>
                </li>
                <li className={`nav-item ${positionOrderTab === "transactionHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("transactionHistory")}>
                        Transaction History
                    </button>
                </li>
            </ul>

            <button
                type="button"
                className={`spot_orders_chipsArrow spot_orders_chipsArrow--right${canRight ? "" : " is-hidden"}`}
                aria-label="Scroll tabs right"
                onClick={() => scrollByAmount(1)}
            >
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </button>
        </div>
    );
}

function orderHistoryLimitOrMarketPrice(item, safeToFixed8) {
    const raw = item?.price;
    if (raw != null && String(raw).trim() !== "") {
        const n = Number(raw);
        if (Number.isFinite(n)) return safeToFixed8(raw, "0");
    }
    const t = String(item?.type || item?.order_type || "").toUpperCase();
    if (t === "MARKET") return "Market";
    return "—";
}

function orderHistoryAvgFillPrice(item, safeToFixed8, filledQtyFn) {
    const filled = filledQtyFn(item);
    const raw = item?.avg_execution_price;
    if (filled <= 0 && (raw == null || String(raw).trim() === "" || Number(raw) === 0)) return "—";
    return safeToFixed8(raw, "—");
}

/** Spot order/tables BUY·SELL — dedicated classes (+ !important) beat .spot_orders_table tbody td. */
function spotSideColorClass(side) {
    const s = String(side ?? "").trim().toUpperCase();
    if (s === "BUY") return "agce-spot-side-buy";
    if (s === "SELL") return "agce-spot-side-sell";
    return "";
}

/** Hero subtitle (side + type/role) with buy/sell tint — matches profile `OrderMobileCardShell` rows. */
function mobileHeroSideTypeSubtitle(side, secondLabel) {
    const typeStr = secondLabel != null && String(secondLabel).trim() !== "" ? String(secondLabel).trim() : "";
    const parts = [side, typeStr].filter((x) => x && x !== "—");
    const line = parts.join(" · ");
    if (!line) return null;
    const s = String(side ?? "").toUpperCase();
    const tone = s === "BUY" ? "orders-mobCard__subtype--buy" : s === "SELL" ? "orders-mobCard__subtype--sell" : "";
    return <span className={`orders-mobCard__subtype ${tone}`.trim()}>{line}</span>;
}

/**
 * ADL risk indicator: 5 equal dots filled by `adl_quantile` (0–5).
 * quantile 0 → all dots shown in light gray (no ADL exposure, layout stays stable).
 */
function AdlIndicator({ quantile }) {
    const raw = Number(quantile);
    const q = Number.isFinite(raw) ? Math.max(0, Math.min(5, Math.round(raw))) : 0;
    const riskLevel = q >= 4 ? "high" : q >= 2 ? "mid" : q >= 1 ? "low" : "none";
    const tooltip = `ADL risk ${q}/5 — higher means your position may be auto-deleveraged first if a counterparty bankruptcy can't be covered by the insurance fund.`;
    return (
        <span className="agce-ft-adl" title={tooltip} role="img" aria-label={tooltip}>
            {[1, 2, 3, 4, 5].map((i) => (
                <span
                    key={i}
                    className={[
                        "agce-ft-adl-dot",
                        i <= q && q > 0 ? "agce-ft-adl-dot--on" : "",
                        i <= q && q > 0 ? `agce-ft-adl-dot--${riskLevel}` : "",
                    ].filter(Boolean).join(" ")}
                />
            ))}
        </span>
    );
}

/** Centered no-data illustration for empty futures history tabs. */
function FuturesHistoryEmpty({ colSpan, mobile = false }) {
    const content = (
        <div className="spot_orders_empty_state" role="status">
            <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
        </div>
    );
    if (mobile) {
        return <div className="no-data-wrapper w-100">{content}</div>;
    }
    return (
        <tr className="no-data-row spot_orders_no_data_row">
            <td colSpan={colSpan}>{content}</td>
        </tr>
    );
}

const TRADE_OPEN_ORDERS_DESKTOP_COLS = 13;
const TRADE_TRADE_HISTORY_DESKTOP_COLS = 9;
const TRADE_ORDER_HISTORY_DESKTOP_COLS = 15;

/**
 * Combine multiple executions at the same trading price into one row
 * (aggregate quantity & fee); preserves first-seen price ordering.
 */
function aggregateOrderHistoryExecutedLegs(item) {
    const executedPrices = Array.isArray(item?.executed_prices) ? item.executed_prices : [];
    if (executedPrices.length === 0) return [];

    const priceGroupKey = (p) => {
        const n = Number(p);
        if (!Number.isFinite(n)) return `s:${String(p ?? "")}`;
        return `n:${n.toFixed(14)}`;
    };

    const keysInOrder = [];
    const buckets = new Map();

    executedPrices.forEach((trade) => {
        const gk = priceGroupKey(trade?.price);
        if (!buckets.has(gk)) {
            keysInOrder.push(gk);
            buckets.set(gk, {
                price: trade?.price,
                sumQty: 0,
                sumFee: 0,
            });
        }
        const b = buckets.get(gk);
        b.sumQty += Number(trade?.quantity) || 0;
        b.sumFee += Number(trade?.fee) || 0;
    });

    return keysInOrder.map((gk, idx) => {
        const b = buckets.get(gk);
        return {
            reactKey: `agg:${gk}:${idx}`,
            price: b.price,
            quantity: b.sumQty,
            fee: b.sumFee,
        };
    });
}

const TradeHistorySection = (props) => {
    const {
        BuyCoinBal,
        SPOT_OPEN_ORDER_KINDS,
        SelectedCoin,
        SellCoinBal,
        buyprice,
        displayLastForHeader,
        formatPriceThousands,
        sellPrice,
        cancelOrder,
        expandedRowIndex,
        fetchSpotWallets,
        formatOrderHistoryDateTime,
        formatSpotOpenOrderDateTime,
        formatTradeHistoryDateTime,
        getOpenOrderPair,
        getOpenOrderStopPrice,
        matchesOpenOrderKind,
        myTrades,
        nineDecimalFormat,
        onSpotOrderHistoryScrollViewport,
        onSpotTradeHistoryScrollViewport,
        openOrderKindTab,
        openOrders,
        orderHistoryMarketLabel,
        orderHistoryQuoteCurrency,
        orderHistoryStatusClass,
        orderHistoryStatusLabel,
        spotOrderLifecycleStatus,
        orderType,
        pastOrders,
        positionOrderTab,
        safeToFixed8,
        setExpandedRowIndex,
        setOpenOrderKindTab,
        setPositionOrderTab,
        setorderType,
        token,
        // Futures live data
        futuresOpenOrders     = [],
        futuresOrderHistory   = [],
        futuresPositions      = [],
        futuresPositionHistory= [],
        futuresTradeHistory   = [],
        futuresWalletHistory  = [],
        futuresWalletHistoryPagination = { total: 0, page: 1, limit: 20, pages: 1 },
        walletHistoryFilters,
        setWalletHistoryFilters,
        cancelFuturesOrder,
        closeFuturesPosition,
        futuresContracts = [],
        futuresAccounts = [],
        futuresAccountPositions = [],
    } = props;

    const [closeModalPos, setCloseModalPos] = useState(null);
    const [closeFieldError, setCloseFieldError] = useState(null);
    const [adjustMarginPos, setAdjustMarginPos] = useState(null);
    const [closingPosId, setClosingPosId] = useState(null);
    const [actionCooldownKeys, setActionCooldownKeys] = useState(() => new Set());
    const cooldownTimersRef = useRef({});

    const ACTION_COOLDOWN_MS = 5000;

    const isActionCoolingDown = useCallback(
        (key) => actionCooldownKeys.has(key),
        [actionCooldownKeys]
    );

    const armActionCooldown = useCallback((key) => {
        if (!key) return;
        setActionCooldownKeys((prev) => new Set(prev).add(key));
        if (cooldownTimersRef.current[key]) {
            clearTimeout(cooldownTimersRef.current[key]);
        }
        cooldownTimersRef.current[key] = window.setTimeout(() => {
            setActionCooldownKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            delete cooldownTimersRef.current[key];
        }, ACTION_COOLDOWN_MS);
    }, []);

    useEffect(() => () => {
        Object.values(cooldownTimersRef.current).forEach(clearTimeout);
        cooldownTimersRef.current = {};
    }, []);

    const handleCancelOrder = useCallback(async (orderId) => {
        const key = `cancel:${orderId}`;
        if (!orderId || !cancelFuturesOrder || isActionCoolingDown(key)) return;
        armActionCooldown(key);
        await cancelFuturesOrder(orderId);
    }, [cancelFuturesOrder, armActionCooldown, isActionCoolingDown]);

    const resolveCloseContract = useCallback(
        (pos) =>
            findFuturesContractInPairs(futuresContracts, pos?.symbol) ||
            (pos?.symbol === SelectedCoin?.symbol ? SelectedCoin : null),
        [futuresContracts, SelectedCoin]
    );

    const handleConfirmClosePosition = useCallback(async ({ orderType, quantity, price }) => {
        const pos = closeModalPos;
        const id = pos?._id;
        const key = `close:${id}`;
        if (!pos || !closeFuturesPosition || isActionCoolingDown(key)) return;
        armActionCooldown(key);
        setClosingPosId(id);
        try {
            const ok = await closeFuturesPosition(pos, {
                orderType,
                quantity,
                ...(orderType === "LIMIT" ? { price } : {}),
            });
            if (ok && ok.ok === false) {
                setCloseFieldError(ok);
                return;
            }
            if (ok === true) {
                setCloseFieldError(null);
                setCloseModalPos(null);
            }
        } finally {
            setClosingPosId(null);
        }
    }, [closeModalPos, closeFuturesPosition, armActionCooldown, isActionCoolingDown]);

    const renderPositionMargin = (pos, margin) => {
        const isolated = isIsolatedMarginMode(pos?.margin_mode);
        return (
            <div className="agce-ft-pos-margin">
                <div className="agce-ft-pos-margin__text">
                    <span className="agce-ft-pos-margin__value">{fmtUsdt(margin)}</span>
                    {pos?.margin_mode ? (
                        <span className="agce-ft-pos-margin__mode">
                            ({formatMarginModeLabel(pos.margin_mode)})
                        </span>
                    ) : null}
                </div>
                {isolated ? (
                    <button
                        type="button"
                        className="agce-ft-pos-margin__edit"
                        aria-label="Adjust isolated margin"
                        onClick={(e) => {
                            e.stopPropagation();
                            setAdjustMarginPos(pos);
                        }}
                    >
                        <i className="ri-edit-box-line" aria-hidden />
                    </button>
                ) : null}
            </div>
        );
    };

    const renderClosePositionActions = (pos) => {
        const id = pos._id;
        const closeCooldownKey = `close:${id}`;
        const isClosing = closingPosId === id;
        const closeDisabled = isClosing || isActionCoolingDown(closeCooldownKey);
        return (
            <button
                type="button"
                className="agce-ft-close-pos__open-btn"
                disabled={closeDisabled}
                onClick={() => setCloseModalPos(pos)}
            >
                {isClosing ? "Closing…" : "Close"}
            </button>
        );
    };

    const baseCc = SelectedCoin?.base_currency || "BTC";
    const quoteCc = SelectedCoin?.quote_currency || "USDT";
    const markRaw = sellPrice !== "" && sellPrice != null ? sellPrice : buyprice !== "" && buyprice != null ? buyprice : displayLastForHeader;
    const fmtTs = (ts) => {
        if (!ts) return "—";
        const m = new Date(ts);
        if (isNaN(m)) return "—";
        const pad = (n) => String(n).padStart(2, "0");
        return `${m.getFullYear()}-${pad(m.getMonth()+1)}-${pad(m.getDate())} ${pad(m.getHours())}:${pad(m.getMinutes())}:${pad(m.getSeconds())}`;
    };
    // Unwrap MongoDB $numberDecimal objects: { "$numberDecimal": "63500" } → "63500"
    const dec = (v) => (v && typeof v === "object" && "$numberDecimal" in v ? v["$numberDecimal"] : v);
    const HISTORY_DECIMAL_CAP = 8;
    const capDecStr = (n, dp = HISTORY_DECIMAL_CAP) => {
        if (!Number.isFinite(n)) return null;
        return parseFloat(n.toFixed(dp)).toString();
    };
    const fmtNum = (v, fallback = "—") => {
        const n = parseFloat(dec(v));
        if (!Number.isFinite(n)) return fallback;
        return capDecStr(n) ?? fallback;
    };
    const sideClass = (side) => {
        const s = String(side ?? "").toUpperCase();
        if (s === "LONG" || s === "BUY") return "agce-spot-side-buy";
        if (s === "SHORT" || s === "SELL") return "agce-spot-side-sell";
        return "";
    };
    const statusClass = (status) => {
        const s = String(status ?? "").toUpperCase();
        if (s === "FILLED" || s === "CLOSED") return "text-success";
        if (s === "CANCELLED" || s === "REJECTED" || s === "LIQUIDATED") return "text-danger";
        return "text-warning";
    };
    const pnlClass = (val) => {
        const n = parseFloat(val);
        if (!Number.isFinite(n) || n === 0) return "";
        return n > 0 ? "agce-ft-pnl--profit" : "agce-ft-pnl--loss";
    };
    // Parse a (possibly $numberDecimal-wrapped) value to a finite number, else NaN.
    const decNum = (v) => {
        const n = parseFloat(dec(v));
        return Number.isFinite(n) ? n : NaN;
    };
    // Derive live position metrics. The API's unrealized_pnl can be stale (0),
    // so recompute from the live mark price when this position is the active pair.
    const computePosition = (pos) => {
        const qty = decNum(pos.quantity);
        const entry = decNum(pos.average_entry_price ?? pos.entry_price);
        // The API's mark_price is authoritative; only fall back to the live
        // stream price for the active pair when the API omits it.
        let mark = decNum(pos.mark_price);
        if (!Number.isFinite(mark) || mark <= 0) {
            const isActive = pos.symbol && SelectedCoin?.symbol && pos.symbol === SelectedCoin.symbol;
            const liveMark = isActive ? Number(String(markRaw ?? "").replace(/,/g, "")) : NaN;
            if (Number.isFinite(liveMark) && liveMark > 0) mark = liveMark;
        }
        const sideSign = String(pos.side ?? "").toUpperCase() === "SHORT" ? -1 : 1;
        let pnl = decNum(pos.unrealized_pnl);
        if ((!Number.isFinite(pnl) || pnl === 0) && Number.isFinite(entry) && Number.isFinite(mark) && Number.isFinite(qty)) {
            pnl = (mark - entry) * qty * sideSign;
        }
        if (!Number.isFinite(pnl)) pnl = 0;
        // Doc: Margin = initial_margin in both modes. Ratio = row.margin_ratio (already %).
        const margin = bindPositionInitialMargin(pos);
        const apiRoe = decNum(pos.roe_pct);
        const roe = Number.isFinite(apiRoe)
            ? apiRoe
            : Number.isFinite(margin) && margin > 0
                ? (pnl / margin) * 100
                : NaN;
        let marginRatio = bindPositionMarginRatioPct(pos);
        if (!Number.isFinite(marginRatio) && isIsolatedMarginMode(pos.margin_mode)) {
            const mm = decNum(pos.maintenance_margin);
            if (Number.isFinite(mm) && Number.isFinite(margin) && margin + pnl > 0) {
                marginRatio = (mm / (margin + pnl)) * 100;
            }
        }
        return { qty, entry, mark, pnl, margin, roe, marginRatio };
    };

    const baseFromSymbol = (sym) => {
        const s = String(sym ?? "");
        const m = s.match(/^([A-Z0-9]+)USDT/i);
        return m ? m[1].toUpperCase() : baseCc;
    };

    const formatCloseReason = (reason, status) => {
        const r = String(reason ?? status ?? "").toUpperCase();
        if (r === "USER") return "Closed manually";
        if (r === "LIQUIDATED" || r === "LIQUIDATION") return "Liquidated";
        if (r === "ADL") return "ADL";
        if (r === "CLOSED") return "Closed";
        if (r === "EXPIRED") return "Expired";
        return reason || status || "—";
    };

    const isLiquidatedPosition = (pos) => {
        const reason = String(pos?.close_reason || "").toUpperCase();
        const status = String(pos?.status || "").toUpperCase();
        return reason === "LIQUIDATION" || reason === "LIQUIDATED" || status === "LIQUIDATED";
    };

    const formatLiqFee = (pos, asset) => {
        const n = decNum(pos?.liq_fee);
        if (!isLiquidatedPosition(pos) || !Number.isFinite(n) || n === 0) return null;
        const feeAsset = pos?.liq_fee_asset || asset || "";
        return { value: capDecStr(n) ?? "0", asset: feeAsset };
    };

    /** Closed / historical position metrics — unwraps $numberDecimal, prefers closed_quantity / close_price. */
    const computeClosedPosition = (pos) => {
        const entry = decNum(pos.average_entry_price ?? pos.entry_price);
        const exit = decNum(pos.average_exit_price ?? pos.close_price);
        let qty = decNum(pos.closed_quantity ?? pos.quantity);
        if ((!Number.isFinite(qty) || qty <= 0) && Number.isFinite(entry) && entry > 0) {
            const initMargin = decNum(pos.initial_margin ?? pos.isolated_margin_allocated);
            const lev = Number(pos.leverage);
            if (Number.isFinite(initMargin) && initMargin > 0 && Number.isFinite(lev) && lev > 0) {
                qty = (initMargin * lev) / entry;
            }
        }
        const rpnlRaw = dec(pos.realized_pnl);
        const rpnl = decNum(pos.realized_pnl);
        const marginAsset = pos.margin_asset || pos.pnl_asset || quoteCc;
        const assetBase = baseFromSymbol(pos.symbol);
        return {
            entry,
            exit,
            qty,
            rpnlRaw,
            rpnl,
            marginAsset,
            assetBase,
            closeLabel: formatCloseReason(pos.close_reason, pos.status),
        };
    };

    const fmtRealizedPnl = (rpnlRaw, marginAsset) => {
        if (rpnlRaw == null || rpnlRaw === "") return "—";
        const n = parseFloat(dec(rpnlRaw));
        if (!Number.isFinite(n)) return `${rpnlRaw} ${marginAsset}`;
        return `${capDecStr(n)} ${marginAsset}`;
    };

    const fmtPrice = (n) => {
        if (!Number.isFinite(n)) return "—";
        const capped = parseFloat(n.toFixed(HISTORY_DECIMAL_CAP));
        return capped.toLocaleString("en-US", {
            maximumFractionDigits: HISTORY_DECIMAL_CAP,
            minimumFractionDigits: 0,
        });
    };
    const fmtUsdt = (n, dp = HISTORY_DECIMAL_CAP) => {
        if (!Number.isFinite(n)) return "—";
        return `${capDecStr(n, dp)} USDT`;
    };
    const fmtPct = (n) => formatMarginRatioPct(n);
    const fmtSignedUsdt = (n) => {
        if (!Number.isFinite(n)) return "—";
        const body = capDecStr(Math.abs(n));
        const signed = n > 0 ? `+${body}` : n < 0 ? `-${body}` : body;
        return `${signed} USDT`;
    };
    const fmtQty = (n, dp = HISTORY_DECIMAL_CAP) => {
        if (!Number.isFinite(n) || n <= 0) return "—";
        return parseFloat(n.toFixed(dp)).toString();
    };
    // Side accent: LONG/BUY → green, SHORT/SELL → red. Used as a left bar on the row.
    const sideAccentColor = (side) => {
        const s = String(side ?? "").toUpperCase();
        if (s === "LONG" || s === "BUY") return "#0ecb81";
        if (s === "SHORT" || s === "SELL") return "#f6465d";
        return "transparent";
    };

    // Avg execution price can be null even for FILLED limit orders → fall back to
    // the limit price when the order is (partially) filled.
    const orderAvgFill = (item) => {
        const a = decNum(item.average_execution_price);
        if (Number.isFinite(a) && a > 0) return fmtPrice(a);
        const filled = decNum(item.filled_quantity);
        if (Number.isFinite(filled) && filled > 0) {
            const fallback = futuresTriggerPrice(item);
            const limit = futuresLimitPrice(item);
            const px = Number.isFinite(limit) && limit > 0 ? limit : fallback;
            if (Number.isFinite(px) && px > 0) return fmtPrice(px);
        }
        return "—";
    };
    const orderFeeDisplay = (item) => {
        const fee = decNum(item.total_fees_paid);
        if (!Number.isFinite(fee)) return "—";
        return `${capDecStr(fee)} ${item.fee_asset ?? "USDT"}`;
    };
    // Attached TP/SL targets on bracket orders (take_profit / stop_loss fields).
    const tpSlText = (item) => {
        const tp = decNum(item.take_profit);
        const sl = decNum(item.stop_loss);
        if (!Number.isFinite(tp) && !Number.isFinite(sl)) return null;
        const parts = [];
        if (Number.isFinite(tp)) parts.push(`TP ${fmtPrice(tp)}`);
        if (Number.isFinite(sl)) parts.push(`SL ${fmtPrice(sl)}`);
        return parts.join(" · ");
    };

    const futuresOrderType = (item) =>
        String(item?.order_type ?? item?.type ?? "").toUpperCase();

    const futuresTriggerPrice = (item) =>
        decNum(item?.trigger_price ?? item?.triggerPrice ?? item?.stop_price ?? item?.stopPrice);

    const futuresLimitPrice = (item) => decNum(item?.price);

    const futuresOrderTypeLabel = (item) => {
        const t = futuresOrderType(item);
        const labels = {
            TAKE_PROFIT_MARKET: "TP Market",
            TAKE_PROFIT_LIMIT: "TP Limit",
            STOP_MARKET: "Stop Market",
            STOP_LIMIT: "Stop Limit",
            CONDITIONAL: "Conditional",
            MARKET: "Market",
            LIMIT: "Limit",
        };
        return labels[t] || t.replace(/_/g, " ");
    };

    const futuresOrderStatusLabel = (item) => {
        const s = String(item?.status ?? "").toUpperCase();
        if (s === "PENDING_TRIGGER") return "Pending Trigger";
        return item?.status ?? "—";
    };

    /** Primary price cell: limit price, trigger price, or Market. */
    const futuresOrderPriceDisplay = (item) => {
        const t = futuresOrderType(item);
        const trigger = futuresTriggerPrice(item);
        const limit = futuresLimitPrice(item);

        if (t === "MARKET") return "Market";
        if (t === "STOP_MARKET" || t === "TAKE_PROFIT_MARKET") {
            return Number.isFinite(trigger) ? fmtPrice(trigger) : "Market";
        }
        if (t === "STOP_LIMIT" || t === "TAKE_PROFIT_LIMIT" || t === "LIMIT") {
            if (Number.isFinite(limit) && limit > 0) return fmtPrice(limit);
        }
        if (t === "CONDITIONAL") {
            const exec = decNum(item?.order_price);
            if (Number.isFinite(exec) && exec > 0) return fmtPrice(exec);
        }
        if (Number.isFinite(trigger) && trigger > 0) return fmtPrice(trigger);
        return "—";
    };

    /** Secondary line under price: trigger for stop/limit TP-SL, or attached TP/SL. */
    const futuresOrderPriceSubtext = (item) => {
        const attached = tpSlText(item);
        if (attached) return attached;

        const t = futuresOrderType(item);
        const trigger = futuresTriggerPrice(item);
        const limit = futuresLimitPrice(item);

        if ((t === "STOP_LIMIT" || t === "TAKE_PROFIT_LIMIT") && Number.isFinite(trigger)) {
            return `Trigger ${fmtPrice(trigger)}`;
        }
        if (t === "CONDITIONAL" && Number.isFinite(trigger)) {
            if (Number.isFinite(limit) && limit > 0) {
                return `Trigger ${fmtPrice(trigger)} · Limit ${fmtPrice(limit)}`;
            }
            return `Trigger ${fmtPrice(trigger)}`;
        }
        if ((t === "STOP_MARKET" || t === "TAKE_PROFIT_MARKET") && Number.isFinite(trigger)) {
            return t === "TAKE_PROFIT_MARKET" ? "TP trigger" : "SL trigger";
        }
        return null;
    };
    // A trade row contains both taker and maker legs. Resolve the values from the
    // current user's perspective (their side, role, fee and realized PnL).
    const currentUserId = (typeof localStorage !== "undefined" && localStorage.getItem("userId")) || null;
    const tradeUserView = (ex) => {
        const takerSide = String(ex.side ?? ex.taker_side ?? "").toUpperCase();
        const flip = (s) => (s === "BUY" ? "SELL" : s === "SELL" ? "BUY" : s);
        const isTaker = currentUserId && ex.taker_user_id && String(ex.taker_user_id) === String(currentUserId);
        const isMaker = currentUserId && ex.maker_user_id && String(ex.maker_user_id) === String(currentUserId);
        if (isMaker && !isTaker) {
            return {
                side: flip(takerSide) || "—",
                role: "Maker",
                fee: decNum(ex.maker_fee),
                pnlRaw: dec(ex.maker_realized_pnl),
                pnl: decNum(ex.maker_realized_pnl),
            };
        }
        // Default to the taker perspective (covers isTaker and unknown-user cases).
        return {
            side: takerSide || "—",
            role: isTaker ? "Taker" : (ex.taker_role ? "Taker" : "—"),
            fee: decNum(ex.taker_fee),
            pnlRaw: dec(ex.taker_realized_pnl),
            pnl: decNum(ex.taker_realized_pnl),
        };
    };

    useEffect(() => {
        const els = document.querySelectorAll('.trade_account_summary_assets');
        if (els.length === 0) return;

        const handleScroll = () => {
            // Apply sticky logic only on mobile view
            if (window.innerWidth > 991) {
                els.forEach(el => el.classList.remove('is-stuck'));
                return;
            }

            // The first element is the outer container which determines the scroll position
            const rect = els[0].getBoundingClientRect();
            // 80px is the top sticky offset. Add a 1px buffer to ensure it triggers correctly.
            if (rect.top <= 81) {
                els.forEach(el => el.classList.add('is-stuck'));
            } else {
                els.forEach(el => el.classList.remove('is-stuck'));
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll);
        handleScroll();
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);

    return (
        <>

            <div className="trade_account_summary_assets">
                <div className="trade_account_summary_assets mt-0">

                    <div className="trade_summary_table_lft mt-0 position_order trade_summary_table_lft_position_order">
                        <div className="top_th_easyop border-0 spot_orders_top">
                            <SpotOrdersPrimaryTabsScroll
                                positionOrderTab={positionOrderTab}
                                setPositionOrderTab={setPositionOrderTab}
                                positionsCount={futuresPositions.length}
                                openOrdersCount={futuresOpenOrders.length}
                            />
                            {/* {positionOrderTab === "orderHistory" || positionOrderTab === "tradeHistory" ? (
                                        <div className="spot_orders_filter_actions px-2 py-1 justify-content-end">
                                            <Link
                                                className="spot_orders_reset"
                                                to={
                                                    positionOrderTab === "orderHistory"
                                                        ? "/user_profile/orders/spot_order_history"
                                                        : "/user_profile/orders/spot_position_history"
                                                }
                                            >
                                                View all
                                            </Link>
                                        </div>
                                    ) : null} */}
                        </div>

                        {positionOrderTab === "openOrders" ? (
                            <SpotOpenOrdersFilterRow
                                SPOT_OPEN_ORDER_KINDS={SPOT_OPEN_ORDER_KINDS}
                                openOrderKindTab={openOrderKindTab}
                                setOpenOrderKindTab={setOpenOrderKindTab}
                                orderType={orderType}
                                setorderType={setorderType}
                            />
                        ) : null}

                        {positionOrderTab === "positions" && (
                            <div className="cnt_table positions active">
                                <div className="desktop_view2">
                                    <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Symbol</th>
                                                    <th>Size</th>
                                                    <th>Entry Price</th>
                                                    <th>Mark Price</th>
                                                    <th>Liq. Price</th>
                                                    <th>Margin Ratio</th>
                                                    <th>Margin</th>
                                                    <th>PNL (ROE%)</th>
                                                    {/* <th>ADL</th> */}
                                                    <th className="yellowcolor">Close Position</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {futuresPositions.length === 0 ? (
                                                    <FuturesHistoryEmpty colSpan={10} />
                                                ) : futuresPositions.map((pos) => {
                                                    const { entry, mark, pnl, margin, roe, marginRatio } = computePosition(pos);
                                                    const pnlStr = fmtSignedUsdt(pnl);
                                                    const roeStr = Number.isFinite(roe) ? `${roe >= 0 ? "+" : ""}${capDecStr(roe)}%` : "—";
                                                    const mrStr = fmtPct(marginRatio);
                                                    return (
                                                        <tr key={pos._id}>
                                                            <td style={{ borderLeft: `3px solid ${sideAccentColor(pos.side)}` }}>
                                                                <div className="fw-bold">{pos.symbol}</div>
                                                                <div className="small text-muted">{pos.leverage}x · {formatMarginModeLabel(pos.margin_mode)}</div>
                                                            </td>
                                                            <td className={sideClass(pos.side)}>
                                                                <div>{fmtNum(pos.quantity)}</div>
                                                                <div className="small">{pos.side}</div>
                                                            </td>
                                                            <td>{fmtPrice(entry)}</td>
                                                            <td>{fmtPrice(mark)}</td>
                                                            <td>{fmtPrice(decNum(pos.liquidation_price))}</td>
                                                            <td>{mrStr}</td>
                                                            <td>{renderPositionMargin(pos, margin)}</td>
                                                            <td>
                                                                <div className={pnlClass(pnl)}>{pnlStr}</div>
                                                                <div className={`small ${pnlClass(pnl)}`}>{roeStr}</div>
                                                            </td>
                                                            {/* <td><AdlIndicator quantile={pos.adl_quantile} /></td> */}
                                                            <td className="agce-ft-close-pos-cell">{renderClosePositionActions(pos)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="order_history_mobile_view twomobile so-mobile">
                                    <div className="d-flex datalist_mbl flex-column">
                                        {futuresPositions.length === 0 ? (
                                            <FuturesHistoryEmpty mobile />
                                        ) : futuresPositions.map((pos) => {
                                            const { entry, mark, pnl, margin, roe, marginRatio } = computePosition(pos);
                                            const pnlStr = fmtSignedUsdt(pnl);
                                            const roeStr = Number.isFinite(roe) ? `${roe >= 0 ? "+" : ""}${capDecStr(roe)}%` : "—";
                                            const mrStr = fmtPct(marginRatio);
                                            return (
                                                <div key={pos._id} style={{ borderLeft: `3px solid ${sideAccentColor(pos.side)}`, borderRadius: "4px" }}>
                                                <OrderMobileCardShell
                                                    desktopColumnCount={10}
                                                    popupTitle="Position details"
                                                    heroPrimary={pos.symbol}
                                                    heroSecondary={
                                                        <span className={sideClass(pos.side)}>{`${pos.side} · ${pos.leverage}x · ${formatMarginModeLabel(pos.margin_mode)}`}</span>
                                                    }
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Size</span>
                                                        <span className={sideClass(pos.side)}>{fmtNum(pos.quantity)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Entry Price</span>
                                                        <span>{fmtPrice(entry)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Mark Price</span>
                                                        <span>{fmtPrice(mark)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Liq. Price</span>
                                                        <span>{fmtPrice(decNum(pos.liquidation_price))}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Margin Ratio</span>
                                                        <span>{mrStr}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Margin</span>
                                                        <span>{renderPositionMargin(pos, margin)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>PNL (ROE%)</span>
                                                        <span className={pnlClass(pnl)}>{pnlStr} ({roeStr})</span>
                                                    </div>
                                                    {/* <div className="so-mobileCard__row">
                                                        <span>ADL</span>
                                                        <span><AdlIndicator quantile={pos.adl_quantile} /></span>
                                                    </div> */}
                                                    <div className="so-mobileCard__row">
                                                        <span className="yellowcolor">Close Position</span>
                                                        <span>{renderClosePositionActions(pos)}</span>
                                                    </div>
                                                </OrderMobileCardShell>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {positionOrderTab === "positionHistory" && (
                            <div className="cnt_table positionHistory active">
                                <div className="desktop_view2">
                                    <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Symbol</th>
                                                    <th>Closed Time</th>
                                                    <th>Side</th>
                                                    <th>Size</th>
                                                    <th>Entry Price</th>
                                                    <th>Exit Price</th>
                                                    <th>Realized PNL</th>
                                                    <th>Liq. Fee</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {futuresPositionHistory.length === 0 ? (
                                                    <FuturesHistoryEmpty colSpan={9} />
                                                ) : futuresPositionHistory.map((pos) => {
                                                    const c = computeClosedPosition(pos);
                                                    const rpnlStr = fmtRealizedPnl(c.rpnlRaw, c.marginAsset);
                                                    const liqFee = formatLiqFee(pos, c.marginAsset);
                                                    return (
                                                        <tr key={pos._id} style={{ borderLeft: `3px solid ${sideAccentColor(pos.side)}` }}>
                                                            <td>
                                                                <div className="fw-bold">{pos.symbol}</div>
                                                                <div className="small text-muted">
                                                                    {pos.leverage}x · {pos.margin_mode}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div>{fmtTs(pos.closed_at)}</div>
                                                                <div className="small text-muted">Opened {fmtTs(pos.opened_at)}</div>
                                                            </td>
                                                            <td className={sideClass(pos.side)}>{pos.side}</td>
                                                            <td className={sideClass(pos.side)}>
                                                                {fmtQty(c.qty)} {c.assetBase}
                                                            </td>
                                                            <td>{fmtPrice(c.entry)}</td>
                                                            <td>{fmtPrice(c.exit)}</td>
                                                            <td className={pnlClass(c.rpnl)}>{rpnlStr}</td>
                                                            <td>
                                                                {liqFee
                                                                    ? (
                                                                        <>
                                                                            {liqFee.value}
                                                                            {liqFee.asset ? (
                                                                                <span className="small text-muted ms-1">{liqFee.asset}</span>
                                                                            ) : null}
                                                                        </>
                                                                    )
                                                                    : "—"}
                                                            </td>
                                                            <td>
                                                                <div>{c.closeLabel}</div>
                                                                <div className={`small ${statusClass(pos.status)}`}>{pos.status || "—"}</div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="order_history_mobile_view twomobile so-mobile">
                                    <div className="d-flex datalist_mbl flex-column">
                                        {futuresPositionHistory.length === 0 ? (
                                            <FuturesHistoryEmpty mobile />
                                        ) : futuresPositionHistory.map((pos) => {
                                            const c = computeClosedPosition(pos);
                                            const rpnlStr = fmtRealizedPnl(c.rpnlRaw, c.marginAsset);
                                            const liqFee = formatLiqFee(pos, c.marginAsset);
                                            return (
                                                <div key={pos._id} style={{ borderLeft: `3px solid ${sideAccentColor(pos.side)}`, borderRadius: "4px" }}>
                                                <OrderMobileCardShell
                                                    desktopColumnCount={9}
                                                    popupTitle="Position history details"
                                                    heroPrimary={pos.symbol}
                                                    heroSecondary={
                                                        <span className={sideClass(pos.side)}>
                                                            {`${pos.side} · ${pos.leverage}x · ${pos.margin_mode}`}
                                                        </span>
                                                    }
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Closed Time</span>
                                                        <span>{fmtTs(pos.closed_at)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Opened Time</span>
                                                        <span>{fmtTs(pos.opened_at)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Size</span>
                                                        <span className={sideClass(pos.side)}>{fmtQty(c.qty)} {c.assetBase}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Entry Price</span>
                                                        <span>{fmtPrice(c.entry)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Exit Price</span>
                                                        <span>{fmtPrice(c.exit)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Realized PNL</span>
                                                        <span className={pnlClass(c.rpnl)}>{rpnlStr}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Liq. Fee</span>
                                                        <span>
                                                            {liqFee
                                                                ? `${liqFee.value}${liqFee.asset ? ` ${liqFee.asset}` : ""}`
                                                                : "—"}
                                                        </span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Status</span>
                                                        <span className={statusClass(pos.status)}>{c.closeLabel}</span>
                                                    </div>
                                                </OrderMobileCardShell>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {positionOrderTab === "openOrders" && (
                            <div className="cnt_table openOrders active">
                                <div className="desktop_view2">
                                    <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Symbol</th>
                                                    <th>Creation Time</th>
                                                    <th>Side</th>
                                                    <th>Price</th>
                                                    <th>Amount</th>
                                                    <th>Filled</th>
                                                    <th>Unfilled</th>
                                                    <th>Fill Price</th>
                                                    <th>Reduce Only</th>
                                                    <th>Type</th>
                                                    <th>TIF</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {futuresOpenOrders.length === 0 ? (
                                                    <FuturesHistoryEmpty colSpan={13} />
                                                ) : futuresOpenOrders.map((item) => {
                                                    const priceDisplay = futuresOrderPriceDisplay(item);
                                                    const priceSubtext = futuresOrderPriceSubtext(item);
                                                    const avgFill = orderAvgFill(item);
                                                    const cancelKey = `cancel:${item._id}`;
                                                    const cancelDisabled = isActionCoolingDown(cancelKey);
                                                    return (
                                                        <tr key={item._id}>
                                                            <td>
                                                                <div className="fw-bold">{item.symbol}</div>
                                                                {item.leverage != null && <div className="small text-muted">{item.leverage}x · {item.margin_mode}</div>}
                                                            </td>
                                                            <td>{fmtTs(item.created_at)}</td>
                                                            <td className={sideClass(item.side)}>
                                                                <div>{item.side}</div>
                                                                {item.position_side && <div className="small text-muted">{item.position_side}</div>}
                                                            </td>
                                                            <td>
                                                                <div>{priceDisplay}</div>
                                                                {priceSubtext && <div className="small text-muted">{priceSubtext}</div>}
                                                            </td>
                                                            <td>{fmtNum(item.quantity)} {baseCc}</td>
                                                            <td>{fmtNum(item.filled_quantity)} {baseCc}</td>
                                                            <td>{fmtNum(item.remaining_quantity)} {baseCc}</td>
                                                            <td>{avgFill}</td>
                                                            <td>{item.reduce_only ? "Yes" : "No"}</td>
                                                            <td>{futuresOrderTypeLabel(item)}</td>
                                                            <td>{item.time_in_force ?? "GTC"}</td>
                                                            <td className={statusClass(item.status)}>{futuresOrderStatusLabel(item)}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-dark btn-sm rounded-pill px-3 static-action-btn"
                                                                    disabled={cancelDisabled}
                                                                    onClick={() => handleCancelOrder(item._id)}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="order_history_mobile_view twomobile so-mobile">
                                    <div className="d-flex datalist_mbl flex-column">
                                        {futuresOpenOrders.length === 0 ? (
                                            <FuturesHistoryEmpty mobile />
                                        ) : futuresOpenOrders.map((item) => {
                                            const priceDisplay = futuresOrderPriceDisplay(item);
                                            const priceSubtext = futuresOrderPriceSubtext(item);
                                            const ts = fmtTs(item.created_at);
                                            const cancelKey = `cancel:${item._id}`;
                                            const cancelDisabled = isActionCoolingDown(cancelKey);
                                            return (
                                                <OrderMobileCardShell
                                                    key={item._id}
                                                    desktopColumnCount={13}
                                                    popupTitle="Order details"
                                                    heroPrimary={item.symbol}
                                                    heroSecondary={`${item.side} · ${futuresOrderTypeLabel(item)} · ${item.leverage ?? "—"}x`}
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Price</span>
                                                        <span className="so-num">{priceDisplay}</span>
                                                    </div>
                                                    {priceSubtext && (
                                                        <div className="so-mobileCard__row">
                                                            <span>Trigger</span>
                                                            <span className="so-num">{priceSubtext}</span>
                                                        </div>
                                                    )}
                                                    <div className="so-mobileCard__row">
                                                        <span>Date</span>
                                                        <span className="so-num">{ts.split(" ")[0]}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Time</span>
                                                        <span className="so-num">{ts.split(" ")[1]}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Amount / Filled</span>
                                                        <span className="so-num">{fmtNum(item.quantity)} / {fmtNum(item.filled_quantity)} {baseCc}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Unfilled</span>
                                                        <span className="so-num">{fmtNum(item.remaining_quantity)} {baseCc}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>TIF</span>
                                                        <span>{item.time_in_force ?? "GTC"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Reduce Only</span>
                                                        <span>{item.reduce_only ? "Yes" : "No"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Status</span>
                                                        <span className={statusClass(item.status)}>{futuresOrderStatusLabel(item)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Action</span>
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="so-cancelBtn static-action-btn"
                                                                disabled={cancelDisabled}
                                                                onClick={() => handleCancelOrder(item._id)}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </span>
                                                    </div>
                                                </OrderMobileCardShell>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {positionOrderTab === "tradeHistory" && (
                            <div className="cnt_table tradeHistory active">
                                <div className="desktop_view2">
                                    <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Market</th>
                                                    <th>Side</th>
                                                    <th>Role</th>
                                                    <th>Price</th>
                                                    <th>Qty</th>
                                                    <th>Fee</th>
                                                    <th>Realized PNL</th>
                                                    <th>Total</th>
                                                    <th>ID</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {futuresTradeHistory.length === 0 ? (
                                                    <FuturesHistoryEmpty colSpan={10} />
                                                ) : futuresTradeHistory.map((ex) => {
                                                    const { side, role, fee, pnl, pnlRaw } = tradeUserView(ex);
                                                    const totalNum = decNum(ex.execution_notional_usd);
                                                    const total = Number.isFinite(totalNum)
                                                        ? totalNum
                                                        : (decNum(ex.execution_price) * decNum(ex.execution_quantity)) || NaN;
                                                    const feeStr = Number.isFinite(fee) ? `${capDecStr(fee)} ${ex.fee_asset ?? "USDT"}` : "—";
                                                    const pnlStr = fmtRealizedPnl(pnlRaw, "USDT");
                                                    return (
                                                        <tr key={ex._id}>
                                                            <td>{fmtTs(ex.executed_at)}</td>
                                                            <td>{ex.symbol}</td>
                                                            <td className={sideClass(side)}>{side}</td>
                                                            <td>{role}</td>
                                                            <td>{fmtPrice(decNum(ex.execution_price))}</td>
                                                            <td>{fmtNum(ex.execution_quantity)} {baseCc}</td>
                                                            <td>{feeStr}</td>
                                                            <td className={pnlClass(pnl)}>{pnlStr}</td>
                                                            <td>{fmtUsdt(total)}</td>
                                                            <td>{ex._id ? `#${ex._id.slice(-6).toUpperCase()}` : "—"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="order_history_mobile_view twomobile so-mobile">
                                    <div className="d-flex datalist_mbl flex-column">
                                        {futuresTradeHistory.length === 0 ? (
                                            <FuturesHistoryEmpty mobile />
                                        ) : futuresTradeHistory.map((ex) => {
                                            const { side, role, fee, pnl, pnlRaw } = tradeUserView(ex);
                                            const totalNum = decNum(ex.execution_notional_usd);
                                            const total = Number.isFinite(totalNum)
                                                ? totalNum
                                                : (decNum(ex.execution_price) * decNum(ex.execution_quantity)) || NaN;
                                            const feeStr = Number.isFinite(fee) ? `${fee} ${ex.fee_asset ?? "USDT"}` : "—";
                                            const pnlStr = fmtRealizedPnl(pnlRaw, "USDT");
                                            const ts = fmtTs(ex.executed_at);
                                            return (
                                                <OrderMobileCardShell
                                                    key={ex._id}
                                                    desktopColumnCount={10}
                                                    popupTitle="Trade details"
                                                    heroPrimary={ex.symbol}
                                                    heroSecondary={`${side}${role !== "—" ? ` · ${role}` : ""}`}
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Price</span>
                                                        <span className="so-num">{fmtPrice(decNum(ex.execution_price))}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Qty</span>
                                                        <span className="so-num">{fmtNum(ex.execution_quantity)} {baseCc}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Date</span>
                                                        <span>{ts.split(" ")[0]}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Time</span>
                                                        <span>{ts.split(" ")[1]}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Fee</span>
                                                        <span>{feeStr}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Realized PNL</span>
                                                        <span className={pnlClass(pnl)}>{pnlStr}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Total</span>
                                                        <span>{fmtUsdt(total)}</span>
                                                    </div>
                                                </OrderMobileCardShell>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {positionOrderTab === "transactionHistory" && walletHistoryFilters && setWalletHistoryFilters ? (
                            <>
                                <FuturesWalletHistoryFilterBar
                                    filters={walletHistoryFilters}
                                    setFilters={setWalletHistoryFilters}
                                    marginAsset={SelectedCoin?.margin_asset || quoteCc}
                                    contractSymbol={SelectedCoin?.symbol}
                                />
                                <div className="cnt_table transactionHistory active">
                                    <div className="desktop_view2">
                                        <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                                            <table className="table table_home spot_orders_table">
                                                <thead>
                                                    <tr>
                                                        <th>Time</th>
                                                        <th>Type</th>
                                                        <th>Amount</th>
                                                        <th>Asset</th>
                                                        <th>Symbol</th>
                                                        <th>Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {futuresWalletHistory.length === 0 ? (
                                                        <FuturesHistoryEmpty colSpan={6} />
                                                    ) : futuresWalletHistory.map((tx) => {
                                                        const amtStr = formatFuturesWalletTxAmount(tx.amount, tx.direction);
                                                        const dirClass = futuresWalletTxAmountClass(tx.direction);
                                                        return (
                                                            <tr key={tx.id}>
                                                                <td>{fmtTs(tx.created_at)}</td>
                                                                <td>{formatFuturesWalletTxType(tx.transaction_type)}</td>
                                                                <td>
                                                                    <span className={dirClass}>{amtStr}</span>
                                                                </td>
                                                                <td>{tx.asset || "—"}</td>
                                                                <td>{tx.symbol || "—"}</td>
                                                                <td>
                                                                    <div>{tx.description || "—"}</div>
                                                                    {tx.reference_id && (
                                                                        <div className="small text-muted">
                                                                            Ref: {String(tx.reference_id).slice(-8).toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        {futuresWalletHistoryPagination.pages > 1 ? (
                                            <div className="d-flex justify-content-between align-items-center px-3 py-2 agce-ft-wallet-hist-pager">
                                                <span className="small text-muted">
                                                    Page {futuresWalletHistoryPagination.page} of {futuresWalletHistoryPagination.pages}
                                                    {" · "}{futuresWalletHistoryPagination.total} records
                                                </span>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        disabled={futuresWalletHistoryPagination.page <= 1}
                                                        onClick={() => setWalletHistoryFilters((p) => ({
                                                            ...p,
                                                            page: Math.max(1, (p.page || 1) - 1),
                                                        }))}
                                                    >
                                                        Previous
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        disabled={futuresWalletHistoryPagination.page >= futuresWalletHistoryPagination.pages}
                                                        onClick={() => setWalletHistoryFilters((p) => ({
                                                            ...p,
                                                            page: Math.min(futuresWalletHistoryPagination.pages, (p.page || 1) + 1),
                                                        }))}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="order_history_mobile_view twomobile so-mobile">
                                        <div className="d-flex datalist_mbl flex-column">
                                            {futuresWalletHistory.length === 0 ? (
                                                <FuturesHistoryEmpty mobile />
                                            ) : futuresWalletHistory.map((tx) => {
                                                const amtStr = formatFuturesWalletTxAmount(tx.amount, tx.direction);
                                                const dirClass = futuresWalletTxAmountClass(tx.direction);
                                                return (
                                                    <OrderMobileCardShell
                                                        key={tx.id}
                                                        desktopColumnCount={6}
                                                        popupTitle="Transaction details"
                                                        heroPrimary={formatFuturesWalletTxType(tx.transaction_type)}
                                                        heroSecondary={fmtTs(tx.created_at)}
                                                    >
                                                        <div className="so-mobileCard__row">
                                                            <span>Amount</span>
                                                            <span className={dirClass}>
                                                                {amtStr} {tx.asset || ""}
                                                            </span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Symbol</span>
                                                            <span>{tx.symbol || "—"}</span>
                                                        </div>
                                                        <div className="so-mobileCard__row">
                                                            <span>Description</span>
                                                            <span>{tx.description || "—"}</span>
                                                        </div>
                                                    </OrderMobileCardShell>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {positionOrderTab === "orderHistory" && (
                            <div className="cnt_table orderHistory active">
                                <div className="desktop_view2">
                                    <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Market</th>
                                                    <th>Side</th>
                                                    <th>Type</th>
                                                    <th>Price</th>
                                                    <th>Avg Fill</th>
                                                    <th>Qty</th>
                                                    <th>Filled</th>
                                                    <th>TIF</th>
                                                    <th>Reduce Only</th>
                                                    <th>Leverage</th>
                                                    <th>Fee</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {futuresOrderHistory.length === 0 ? (
                                                    <FuturesHistoryEmpty colSpan={13} />
                                                ) : futuresOrderHistory.map((item) => {
                                                    const priceDisplay = futuresOrderPriceDisplay(item);
                                                    const priceSubtext = futuresOrderPriceSubtext(item);
                                                    const reason = item.cancelled_reason || null;
                                                    const avgFill = orderAvgFill(item);
                                                    const lev = item.leverage != null ? `${item.leverage}x · ${item.margin_mode ?? ""}`.trim() : "—";
                                                    const fee = orderFeeDisplay(item);
                                                    return (
                                                        <tr key={item._id}>
                                                            <td>{fmtTs(item.created_at)}</td>
                                                            <td>{item.symbol}</td>
                                                            <td className={sideClass(item.side)}>
                                                                <div>{item.side}</div>
                                                                {item.position_side && <div className="small text-muted">{item.position_side}</div>}
                                                            </td>
                                                            <td>{futuresOrderTypeLabel(item)}</td>
                                                            <td>
                                                                <div>{priceDisplay}</div>
                                                                {priceSubtext && <div className="small text-muted">{priceSubtext}</div>}
                                                            </td>
                                                            <td>{avgFill}</td>
                                                            <td>{fmtNum(item.quantity)} {baseCc}</td>
                                                            <td>{fmtNum(item.filled_quantity)} {baseCc}</td>
                                                            <td>{item.time_in_force ?? "—"}</td>
                                                            <td>{item.reduce_only ? "Yes" : "No"}</td>
                                                            <td>{lev}</td>
                                                            <td>{fee}</td>
                                                            <td className={statusClass(item.status)}>
                                                                <div>{futuresOrderStatusLabel(item)}</div>
                                                                {/* {reason && <div className="small text-muted">{reason}</div>} */}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="order_history_mobile_view twomobile so-mobile">
                                    <div className="d-flex datalist_mbl flex-column">
                                        {futuresOrderHistory.length === 0 ? (
                                            <FuturesHistoryEmpty mobile />
                                        ) : futuresOrderHistory.map((item) => {
                                            const priceDisplay = futuresOrderPriceDisplay(item);
                                            const priceSubtext = futuresOrderPriceSubtext(item);
                                            const ts = fmtTs(item.created_at);
                                            const reason = item.cancelled_reason || null;
                                            return (
                                                <OrderMobileCardShell
                                                    key={item._id}
                                                    desktopColumnCount={13}
                                                    popupTitle="Order history details"
                                                    heroPrimary={item.symbol}
                                                    heroSecondary={`${item.side} · ${futuresOrderTypeLabel(item)} · ${item.leverage ?? "—"}x`}
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Price</span>
                                                        <span className="so-num">{priceDisplay}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Avg Fill</span>
                                                        <span className="so-num">{orderAvgFill(item)}</span>
                                                    </div>
                                                    {priceSubtext && (
                                                        <div className="so-mobileCard__row">
                                                            <span>Trigger</span>
                                                            <span className="so-num">{priceSubtext}</span>
                                                        </div>
                                                    )}
                                                    <div className="so-mobileCard__row">
                                                        <span>Date</span>
                                                        <span>{ts.split(" ")[0]}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Time</span>
                                                        <span>{ts.split(" ")[1]}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Qty / Filled</span>
                                                        <span>{fmtNum(item.quantity)} / {fmtNum(item.filled_quantity)} {baseCc}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>TIF</span>
                                                        <span>{item.time_in_force ?? "—"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Reduce Only</span>
                                                        <span>{item.reduce_only ? "Yes" : "No"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Fee</span>
                                                        <span>{orderFeeDisplay(item)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Status</span>
                                                        <span className={statusClass(item.status)}>{futuresOrderStatusLabel(item)}
                                                            {/* {reason ? ` · ${reason}` : ""} */}</span>
                                                    </div>
                                                </OrderMobileCardShell>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="assets_right d-none d-lg-block assets_panel_desktop agce-ft-acct-rail">
                    <FuturesAccountPanel
                        token={token}
                        futuresAccounts={futuresAccounts}
                        futuresPositions={futuresAccountPositions}
                        contractReady={!!SelectedCoin?.symbol}
                    />
                </div>
            </div>

            {closeModalPos && (
                <FuturesClosePositionModal
                    pos={closeModalPos}
                    contract={resolveCloseContract(closeModalPos)}
                    loading={closingPosId === closeModalPos._id}
                    fieldError={closeFieldError}
                    onClose={() => {
                        if (closingPosId) return;
                        setCloseFieldError(null);
                        setCloseModalPos(null);
                    }}
                    onConfirm={handleConfirmClosePosition}
                />
            )}
            {adjustMarginPos && isIsolatedMarginMode(adjustMarginPos.margin_mode) ? (
                <FuturesAdjustIsolatedMarginModal
                    pos={adjustMarginPos}
                    onClose={() => setAdjustMarginPos(null)}
                />
            ) : null}
        </>
    );
};

export default memo(TradeHistorySection);
